import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDrawStore, useDrawOptions } from '../store';

import { getDrawQuery } from '@queries/draws';
import { hydrateWithTranslations } from '@services/TranslatorService';
import type { DrawOpponent } from '@shared-types/draw.types';
import { IS_SIMULATION, IS_TEST } from '@utilities/browser';
import { orderBy, uniqBy } from '@utilities/collection-utils';

export const useDrawPolling = () => {
  const {
    options: { drawId, interval = 50000 },
  } = useDrawOptions();

  const draw = useDrawStore((state) => state.draw);
  const setDraw = useDrawStore((state) => state.setDraw);

  const { data: initialDraw } = useQuery({
    ...getDrawQuery(drawId, ['TEAMS', 'ROUNDS', 'GROUPS', 'POTS']),
  });

  const { data } = useQuery({
    ...getDrawQuery(drawId, ['TEAMS']),
    refetchInterval: !IS_TEST && (IS_SIMULATION ? 5000 : interval),
    enabled: !!initialDraw,
  });

  const mergedDraw = useMemo(
    () => ({
      ...data,
      rounds: data?.rounds?.map((round) => {
        const initialRound = initialDraw?.rounds?.find((r) => r.round.id === round.roundId);
        return {
          ...round,
          pots: initialRound?.pots,
          round: initialRound?.round,
          result: {
            groups: round.result?.groups?.map((group) => ({
              ...group,
              group: initialRound?.result?.groups?.find((g) => g.group?.id === group.groupId)?.group,
            })),
          },
        };
      }),
    }),
    [data, initialDraw],
  );

  useEffect(() => {
    if (!mergedDraw) return;

    setDraw(mergedDraw);
  }, [mergedDraw, setDraw]);

  return draw;
};

export const useDrawPots = () => {
  const { filters } = useDrawFilters();
  const rounds = useDrawStore((state) => state.draw.rounds);
  const currentPots = useMemo(
    () =>
      orderBy(
        rounds.map(({ pots, round }) => ({ pots, round })),
        (a) => a.round?.orderInCompetition,
      ),
    [rounds],
  );

  const filteredPots = useMemo(
    () =>
      currentPots
        .map((potGroup) => ({
          ...potGroup,
          pots: potGroup.pots?.filter((p) => !filters.leagueId || p.leagueId === filters.leagueId),
        }))
        .filter((potGroup) => potGroup.pots?.length),
    [filters, currentPots],
  );

  return { filteredPots, pots: currentPots };
};

export const useDrawResults = () => {
  const { filters } = useDrawFilters();
  const rounds = useDrawStore((state) => state.draw.rounds);
  const results = useMemo(
    () =>
      orderBy(
        rounds.map(({ result, round }) => ({ groups: result?.groups, round })),
        (a) => a.round?.orderInCompetition,
      ),
    [rounds],
  );

  const filteredResults = useMemo(
    () =>
      results.map((res) => ({
        ...res,
        groups: res.groups?.filter((g) => !filters.leagueId || g.group?.league?.id === filters.leagueId),
      })),
    [filters.leagueId, results],
  );

  return { results, filteredResults };
};

export const useDrawFilters = () => {
  const filters = useDrawStore((state) => state.filters);
  const setFilters = useDrawStore((state) => state.setFilters);

  return { filters, setFilters };
};

export const useDrawExtracted = (teamId: string, isResult?: boolean) => {
  const rounds = useDrawStore((state) => state.draw.rounds);

  const value = useMemo(() => {
    if (!teamId) return false;

    if (isResult) return true;

    return rounds?.some((round) =>
      round.result.groups?.some((g) => g.teamSlots?.some((slot) => slot.teams?.some((t) => t.id === teamId))),
    );
  }, [isResult, rounds, teamId]);

  const alreadyExtracted = useRef<boolean>(null);
  const [extracted, setExtracted] = useState(value);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (value === alreadyExtracted.current) return;

    if (alreadyExtracted.current === null) {
      setExtracted(value);
    } else {
      setExtracting(true);
    }

    alreadyExtracted.current = value;

    const timeout = setTimeout(() => {
      setExtracting(false);
      setExtracted(value);
    }, 2000);

    return () => {
      clearTimeout(timeout);
    };
  }, [value]);

  return { extracted, extracting };
};

export const useLeagues = () => {
  const { pots } = useDrawPots();

  const leagues = useMemo(
    () =>
      orderBy(
        uniqBy(pots.flatMap((pg) => pg.pots.map((p) => hydrateWithTranslations(p.league))).filter(Boolean), 'id'),
        'order',
      ),
    [pots],
  );

  const savedExtractingLeague = useRef<string>(null);
  const { results } = useDrawResults();
  const { filters, setFilters } = useDrawFilters();

  const extractingLeague = useMemo(
    () =>
      results?.[0]?.groups?.findLast(
        (g) => (!!g.teamSlots?.length && g.teamSlots.find((t) => !t.teamIds?.length)) || !g.teamSlots.length,
      )?.group?.league?.id,
    [results],
  );

  const setSelectedLeague = useCallback(
    (leagueId: string) => {
      setFilters({ leagueId });
    },
    [setFilters],
  );

  useEffect(() => {
    if (!leagues?.length) return;

    const availableTeams = pots.flatMap((p) => p.pots?.flatMap((i) => i.teamIds))?.length;
    const extractedTeams = results[0].groups?.flatMap((p) => p.teamSlots)?.length;

    const newSelectedLeagueId = extractingLeague ?? leagues.at(extractedTeams === availableTeams ? 0 : -1)?.id;

    if (!newSelectedLeagueId || savedExtractingLeague.current === newSelectedLeagueId) return;
    savedExtractingLeague.current = newSelectedLeagueId;

    setSelectedLeague(newSelectedLeagueId);
  }, [extractingLeague, leagues, setSelectedLeague, pots, results]);

  return { leagues, selectedLeague: filters?.leagueId, setSelectedLeague };
};

export const useSortedOpponents = (opponents: DrawOpponent[]) => {
  const { numberOfOpponentsPerPot } = useDrawConfiguration();
  const { filteredPots: potsGroups } = useDrawPots();

  return useMemo(() => {
    if (!potsGroups || !opponents) return opponents;

    // Create a Map with {'potId': 'numOrder'}
    const potOrderMap = potsGroups[0]?.pots.reduce((map, pot) => {
      map[pot.potId] = pot.numOrder;
      return map;
    }, {});

    // FSP cannot guarantee  that PotId will always be present so we modify our opponents array to put potId for each opponent object
    //because we know the sequence two teams from pot 1 the second two pot 2 and so on...
    const opponentsWithPotId = opponents.map((opponent, index) => {
      if (!opponent.potId) {
        const pot = potsGroups[0].pots[Math.floor(index / numberOfOpponentsPerPot)];
        return { ...opponent, potId: pot.potId };
      }
      return opponent;
    });

    // Sort logic by first Pot then second and so on... then we make sure that HOME comes before Away always.
    return opponentsWithPotId.toSorted((a, b) => {
      const orderA = potOrderMap[a.potId];
      const orderB = potOrderMap[b.potId];

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      if (a.location === 'HOME' && b.location === 'AWAY') {
        return -1;
      } else if (a.location === 'AWAY' && b.location === 'HOME') {
        return 1;
      }

      return 0;
    });
  }, [opponents, potsGroups, numberOfOpponentsPerPot]);
};

export const useDrawnTeamId = () => useDrawStore((state) => state.draw.lastDrawnTeamId);

export const useDrawConfiguration = () => {
  const config = useDrawStore((state) => state.draw.teamSlotConfiguration);
  const { filteredPots: potsGroups } = useDrawPots();

  return useMemo(
    () => ({
      ...config,
      numberOfOpponents: config?.numberOfOpponentsPerPot * potsGroups?.[0]?.pots?.length,
    }),
    [config, potsGroups],
  );
};

export const useDrawButton = () => {
  const state = useDrawStore((state) => state.button);
  const showButton = useDrawStore((state) => state.showButton);
  const hideButton = useDrawStore((state) => state.hideButton);

  return useMemo(
    () => ({
      ...state,
      showButton,
      hideButton,
    }),
    [state, showButton, hideButton],
  );
};
